import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FolderKanban,
  Loader2,
  Server,
  Database,
  Cpu,
  Layers,
  Pencil,
  Code,
  Rocket,
  FlaskConical,
  MessageSquare,
  PauseCircle,
  Users,
  UserCog,
  GitBranch,
  Globe,
  Lock,
  Unlock,
} from "lucide-react";
import { AdminGetAllProject } from "../../AdminServices/AdminProjectService";
import CreateProjectModal from "../AdminModals/CreateProjectModal";
import frameBg from "../../../../Assets/FrameBGDashboard.png";
import { getImageUrl } from "../../../Utils/urlHelper";
import { globalSearch } from "../../../Utils/globalSearch";

const AdminAllProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showEnvironmentModal, setShowEnvironmentModal] = useState(false);
  const [selectedProjectEnvironments, setSelectedProjectEnvironments] =
    useState([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState(null);
  const [showSensitiveData, setShowSensitiveData] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await AdminGetAllProject();

        if (res.data.status === "Success") {
          setProjects(res.data.data);
        } else {
          setError("Failed to load project data.");
        }
      } catch {
        setError("Something went wrong while loading projects.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSearch = (value) => {
    setSearchQuery(value);
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
  };

  const handleEditClick = (project) => {
    setSelectedProject(project);
    setShowModal(true);
  };

  const getStatusBadge = (status) => {
    const s = status?.toLowerCase();

    switch (s) {
      case "development":
        return {
          color: "bg-blue-100 text-blue-700",
          icon: <Code size={12} />,
        };

      case "uat":
        return {
          color: "bg-yellow-100 text-yellow-700",
          icon: <FlaskConical size={12} />,
        };

      case "production":
        return {
          color: "bg-green-100 text-green-700",
          icon: <Rocket size={12} />,
        };

      case "discussion":
        return {
          color: "bg-purple-100 text-purple-700",
          icon: <MessageSquare size={12} />,
        };

      case "hold":
        return {
          color: "bg-red-100 text-red-700",
          icon: <PauseCircle size={12} />,
        };

      default:
        return {
          color: "bg-gray-100 text-gray-600",
          icon: null,
        };
    }
  };

  const filteredProjects = globalSearch(projects, searchQuery, [
    "projectName",
    "projectDescription",
    "projectType",
    "currentStatus",
    "projectMangers",
  ]);

  const finalProjects = filteredProjects.filter((p) =>
    statusFilter
      ? p.currentStatus?.toLowerCase() === statusFilter.toLowerCase()
      : true,
  );

  const toggleSensitiveData = (projectUID, type) => {
    setShowSensitiveData((prev) => ({
      ...prev,
      [`${projectUID}-${type}`]: !prev[`${projectUID}-${type}`],
    }));
  };

  const maskValue = (value) => {
    if (!value) return "N/A";

    const str = value.toString();

    if (str.length <= 2) return str;

    return `${"X".repeat(str.length - 2)}${str.slice(-2)}`;
  };

  return (
    <>
      <div className="min-h-screen w-full">
        <div
          className="relative rounded-lg overflow-hidden shadow-md text-white mx-0 mt-6"
          style={{
            backgroundImage: `url(${frameBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="p-6 flex justify-between items-center">
            <h2 className="text-2xl font-semibold">All Projects</h2>

            <button
              onClick={() => setShowModal(true)}
              className="bg-[#003a63] text-white px-5 py-2 rounded-lg"
            >
              + Create Project
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 px-6 mt-3">
          {/* LEFT SIDE */}
          <div className="flex flex-wrap items-center gap-3">
            {/* SEARCH */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-3 pr-3 py-2 text-sm border rounded-md w-60 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>

            {/* STATUS FILTER */}
            <select
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="px-3 py-2 text-sm border rounded-md"
            >
              <option value="">All Status</option>
              <option value="Development">Development</option>
              <option value="UAT">UAT</option>
              <option value="Production">Production</option>
              <option value="Discussion">Discussion</option>
              <option value="Hold">Hold</option>
            </select>
          </div>
        </div>

        <div className="px-6 pt-4 pb-6">
          {loading && (
            <div className="flex justify-center mt-20">
              <Loader2 className="animate-spin text-[#0061A4]" size={40} />
            </div>
          )}

          {error && (
            <p className="text-center text-red-500 font-medium">{error}</p>
          )}

          {!loading && !error && (
            <>
              {finalProjects.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center text-gray-500">
                  <FolderKanban size={40} className="mb-3 text-gray-400" />

                  <h3 className="text-lg font-semibold text-gray-700">
                    No Projects Found
                  </h3>

                  <p className="text-sm mt-1">
                    {searchQuery || statusFilter
                      ? "No matching projects for your filters"
                      : "No projects available"}
                  </p>

                  {(searchQuery || statusFilter) && (
                    <button
                      onClick={() => {
                        setSearchQuery("");
                        setStatusFilter("");
                      }}
                      className="mt-3 text-blue-600 text-sm hover:underline"
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {finalProjects.map((p) => (
                    <div className="relative bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group flex flex-col h-full">
                      {/* <div className="absolute left-0 top-0 h-full w-1.5 bg-gradient-to-b from-blue-500 to-cyan-400"></div> */}
                      <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-blue-500 to-cyan-400"></div>
                      <div className="p-5 pl-6 flex flex-col h-full">
                        {/*  TOP SECTION */}
                        <div
                          key={p.projectUID}
                          onClick={() =>
                            navigate(`/Admin-Project-Team/${p.projectUID}`, {
                              state: { project: p },
                            })
                          }
                          className="flex items-center gap-3 mb-4 cursor-pointer"
                        >
                          {/* Logo */}
                          <div className="w-28 h-14 rounded-lg bg-gray-100 flex items-center justify-center">
                            {p.logoURL ? (
                              <img
                                src={getImageUrl(p.logoURL)}
                                alt="logo"
                                className="w-28 h-10 object-contain"
                              />
                            ) : (
                              <FolderKanban
                                className="text-blue-500"
                                size={22}
                              />
                            )}
                          </div>

                          {/* Name + Status */}
                          <div className="flex-1">
                            <h2 className="text-md font-semibold text-gray-800 group-hover:text-blue-600 transition">
                              {p.projectName}
                            </h2>

                            <p className="text-xs text-gray-400 mt-1">
                              {p.projectDescription || "No description"}
                            </p>
                          </div>

                          {/* Status Dot */}
                          {(() => {
                            const badge = getStatusBadge(p.currentStatus);
                            return (
                              <div
                                className={`flex items-center gap-1 text-xs px-3 py-1 rounded-full font-medium ${badge.color}`}
                              >
                                {badge.icon}
                                {p.currentStatus || "N/A"}
                              </div>
                            );
                          })()}
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between text-gray-600">
                            <span className="flex items-center gap-2">
                              <Layers size={14} /> Project Type
                            </span>
                            <span className="font-medium text-gray-800">
                              {p.projectType || "N/A"}
                            </span>
                          </div>

                          <div className="flex justify-between text-gray-600">
                            <span className="flex items-center gap-2">
                              <Cpu size={14} /> Tech Stack
                            </span>
                            <span className="font-medium text-gray-800 truncate max-w-[60%]">
                              {p.techStack || "N/A"}
                            </span>
                          </div>

                          <div className="flex justify-between items-start text-gray-600">
                            <span className="flex items-center gap-2">
                              <Server size={14} /> Server
                            </span>

                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-800">
                                {showSensitiveData[`${p.projectUID}-server`]
                                  ? p.ipAddress || "N/A"
                                  : maskValue(p.ipAddress || "")}
                              </span>

                              <button
                                onClick={() =>
                                  toggleSensitiveData(p.projectUID, "server")
                                }
                                className="text-gray-500 hover:text-blue-600"
                              >
                                {showSensitiveData[`${p.projectUID}-server`] ? (
                                  <Unlock size={15} />
                                ) : (
                                  <Lock size={15} />
                                )}
                              </button>
                            </div>
                          </div>

                          <div className="flex justify-between items-start text-gray-600">
                            <span className="flex items-center gap-2">
                              <Database size={14} /> Database
                            </span>

                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-800">
                                {showSensitiveData[`${p.projectUID}-db`]
                                  ? p.dbDetails || "N/A"
                                  : maskValue(p.dbDetails || "")}
                              </span>

                              <button
                                onClick={() =>
                                  toggleSensitiveData(p.projectUID, "db")
                                }
                                className="text-gray-500 hover:text-blue-600"
                              >
                                {showSensitiveData[`${p.projectUID}-db`] ? (
                                  <Unlock size={15} />
                                ) : (
                                  <Lock size={15} />
                                )}
                              </button>
                            </div>
                          </div>
                          <div className="flex justify-between text-gray-600">
                            <span className="flex items-center gap-2">
                              <Users size={14} /> Team Size
                            </span>
                            <span className="font-medium text-gray-800">
                              {p.teamSize || "N/A"}
                            </span>
                          </div>

                          <div className="flex justify-between text-gray-600">
                            <span className="flex items-center gap-2">
                              <UserCog size={14} /> Project Mangers
                            </span>
                            <span className="font-medium text-gray-800">
                              {p.projectMangers || "N/A"}
                            </span>
                          </div>

                          <div className="flex justify-between text-gray-600">
                            <span className="flex items-center gap-2">
                              <GitBranch size={14} /> Git Repository
                            </span>
                            <span className="font-medium text-gray-800 flex flex-col items-end text-right">
                              {p.gitRepoMethod
                                ? p.gitRepoMethod.split(",").map((repo, i) => {
                                    const cleanRepo = repo.trim();

                                    return (
                                      <a
                                        key={i}
                                        href={
                                          cleanRepo.startsWith("http")
                                            ? cleanRepo
                                            : `https://${cleanRepo}`
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:underline truncate max-w-[180px]"
                                        title={cleanRepo}
                                      >
                                        {cleanRepo.split("/").pop()}
                                      </a>
                                    );
                                  })
                                : "N/A"}
                            </span>
                          </div>
                        </div>
                        {/*  BUTTON */}
                        <div className="mt-auto pt-4 flex items-center justify-between">
                          {/* Open Application */}
                          {p.environments && p.environments.length > 0 ? (
                            <button
                              onClick={() => {
                                setSelectedProjectEnvironments(p.environments);

                                setSelectedEnvironment(null);

                                setShowEnvironmentModal(true);
                              }}
                              className="text-sm font-medium text-blue-600 hover:underline flex items-center gap-1"
                            >
                              <Globe size={14} />
                              Open Application
                            </button>
                          ) : (
                            <span className="text-sm text-gray-400 flex items-center gap-1 cursor-not-allowed">
                              <Globe size={14} />
                              Open Application
                            </span>
                          )}

                          {/* Edit Icon */}
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditClick(p);
                            }}
                            className="flex items-center gap-1 text-gray-500 hover:text-blue-600 cursor-pointer transition"
                          >
                            <Pencil size={16} />
                            <span className="text-xs font-medium">Edit</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <CreateProjectModal
          show={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedProject(null);
          }}
          editData={selectedProject}
        />
      </div>
      {/* Environment Details Modal */}
      {showEnvironmentModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fadeIn">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0061A4] to-[#00ACED] px-6 py-4 flex items-center justify-between">
              <h2 className="text-white text-lg font-semibold">
                Environment Details
              </h2>

              <button
                onClick={() => setShowEnvironmentModal(false)}
                className="text-white text-xl"
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div className="p-6">
              {!selectedEnvironment ? (
                <>
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">
                    Select Environment
                  </h3>

                  <div className="space-y-3">
                    {selectedProjectEnvironments.map((env) => (
                      <div
                        key={env.environmentUID}
                        className="border border-gray-200 rounded-xl p-4 hover:border-[#00ACED] hover:bg-blue-50 transition"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div
                            className="flex-1 cursor-pointer"
                            onClick={() =>
                              setSelectedEnvironment({
                                ...env,
                                loginType: Number(env.loginType),
                              })
                            }
                          >
                            {/* Top Row */}
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold text-gray-800">
                                {env.environmentType}
                              </h4>

                              <span
                                className={`text-[10px] px-2.5 py-1 rounded-full font-medium ${
                                  Number(env.loginType) === 1
                                    ? "bg-green-100 text-green-700"
                                    : "bg-orange-100 text-orange-700"
                                }`}
                              >
                                {Number(env.loginType) === 1
                                  ? "CLS"
                                  : "Non-CLS"}
                              </span>
                            </div>

                            {/* URL */}
                            <p className="text-xs text-gray-500 mt-2 break-all">
                              {env.appURL}
                            </p>
                          </div>

                          {/* Open Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();

                              const url = env.appURL?.startsWith("http")
                                ? env.appURL
                                : `https://${env.appURL}`;

                              window.open(url, "_blank");
                            }}
                            className="bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white text-xs px-4 py-2 rounded-lg hover:opacity-90 whitespace-nowrap"
                          >
                            Open Application
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="space-y-5">
                  {/* Back */}
                  <button
                    onClick={() => setSelectedEnvironment(null)}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    ← Back to Environments
                  </button>

                  {/* Environment Type */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                      Environment Type
                    </label>

                    <div className="border rounded-lg px-4 py-3 bg-gray-50 text-sm">
                      {selectedEnvironment.environmentType || "-"}
                    </div>
                  </div>

                  {/* Login Type */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                      Login Type
                    </label>

                    <div className="border rounded-lg px-4 py-3 bg-gray-50 text-sm">
                      {Number(selectedEnvironment.loginType) === 1
                        ? "CLS Based"
                        : "Non-CLS Based"}
                    </div>
                  </div>

                  {/* URL */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                      Application URL
                    </label>

                    <div className="border rounded-lg px-4 py-3 bg-gray-50 text-sm break-all">
                      {selectedEnvironment.appURL || "-"}
                    </div>
                  </div>

                  {/* Non CLS */}
                  {Number(selectedEnvironment.loginType) === 2 && (
                    <>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                          Username / Email
                        </label>

                        <div className="border rounded-lg px-4 py-3 bg-gray-50 text-sm">
                          {selectedEnvironment.username || "-"}
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                          Password
                        </label>

                        <div className="border rounded-lg px-4 py-3 bg-gray-50 text-sm">
                          {selectedEnvironment.password || "-"}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Launch */}
                  <button
                    onClick={() => {
                      const url = selectedEnvironment.appURL?.startsWith("http")
                        ? selectedEnvironment.appURL
                        : `https://${selectedEnvironment.appURL}`;

                      window.open(url, "_blank");
                    }}
                    className="w-full bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white py-3 rounded-xl font-medium hover:opacity-90"
                  >
                    Open Application
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminAllProjects;
